// Trainer controls and student timer for lecture mode.
import type { RefObject } from 'react';
import type { Color } from '../../types';

type LecturePositionPanelProps = {
  turn: Color;
  onResetPosition: () => void;
  onClearBoard: () => void;
  onSetTurn: (color: Color) => void;
};

export function LecturePositionPanel({ turn, onResetPosition, onClearBoard, onSetTurn }: LecturePositionPanelProps) {
  return (
    <section className="panel">
      <h2>Редактор позиции</h2>
      <div className="editor-actions">
        <button className="secondary" onClick={onResetPosition}>Начальная</button>
        <button className="danger" onClick={onClearBoard}>Очистить</button>
      </div>
      <div className="editor-actions">
        <button className={turn === 'w' ? 'primary' : 'secondary'} onClick={() => onSetTurn('w')}>♔ Белые</button>
        <button className={turn === 'b' ? 'primary' : 'secondary'} onClick={() => onSetTurn('b')}>♚ Чёрные</button>
      </div>
    </section>
  );
}

type LectureTaskTimerPanelProps = {
  recording: boolean;
  recorded: string[];
  sequence?: string;
  seconds: number;
  remainingMs: number;
  timerActive: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  onStartRecording: () => void;
  onFinishRecording: () => void;
  onRetry: () => void;
  onAdjustTimer: (delta: number) => void;
  onSecondsChange: (seconds: number) => void;
  onStartTimer: () => void;
  onStopTimer: () => void;
  formatClock: (ms: number) => string;
};

export function LectureTaskTimerPanel({
  recording,
  recorded,
  sequence,
  seconds,
  remainingMs,
  timerActive,
  inputRef,
  onStartRecording,
  onFinishRecording,
  onRetry,
  onAdjustTimer,
  onSecondsChange,
  onStartTimer,
  onStopTimer,
  formatClock
}: LectureTaskTimerPanelProps) {
  return (
    <div className="board-session-tools">
      <section className="panel compact-panel task-panel">
        <div className="compact-panel-heading">
          <h2>Задача</h2>
          <div className="compact-panel-actions">
            {!recording ? (
              <button className="secondary" onClick={onStartRecording}>Записать решение</button>
            ) : (
              <button className="danger" onClick={onFinishRecording}>Завершить запись</button>
            )}
            <button className="secondary" onClick={onRetry}>Заново</button>
          </div>
        </div>
        <div className="task-preview task-preview-compact">{recorded.length ? recorded.join(', ') : sequence || 'Не задано'}</div>
      </section>

      <section className="panel compact-panel timer-panel">
        <div className="compact-panel-heading">
          <h2>Таймер задачи</h2>
          <div
            className="timer-readout"
            onWheel={(event) => {
              event.preventDefault();
              onAdjustTimer(event.deltaY < 0 ? 1 : -1);
            }}
            title={timerActive ? 'Колесо изменяет оставшееся время на 1 секунду' : 'Колесо изменяет время на 1 секунду'}
          >
            {timerActive ? formatClock(remainingMs) : '00:00'}
          </div>
        </div>
        <div className="timer-panel-controls">
          <label htmlFor="lecture-seconds">Время, секунды</label>
          <input
            ref={inputRef}
            id="lecture-seconds"
            type="number"
            min={1}
            max={3600}
            value={seconds}
            onChange={(event) => onSecondsChange(Number(event.target.value))}
            title={timerActive ? 'Измените значение и нажмите «Обновить»' : undefined}
          />
          <div className="inline">
            <button className="primary" onClick={onStartTimer}>{timerActive ? 'Обновить' : 'Старт'}</button>
            <button className="danger" onClick={onStopTimer}>Стоп</button>
          </div>
        </div>
      </section>
    </div>
  );
}

type LectureStudentTimerProps = {
  active: boolean;
  remainingMs: number;
  formatClock: (ms: number) => string;
};

export function LectureStudentTimer({ active, remainingMs, formatClock }: LectureStudentTimerProps) {
  return (
    <div className="panel lecture-timer-student">
      <div className="muted">Таймер задачи</div>
      <div className="timer-large">{active ? formatClock(remainingMs) : '00:00'}</div>
    </div>
  );
}
